import React, { useState, useEffect } from 'react';
import { PhotoIcon, SparklesIcon, SwatchIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { HexColorPicker } from 'react-colorful';
import { notify } from '../../../utils/notify.jsx';
import { useImageUpload } from '../modals/ImageEditor/hooks/useImageUpload';
import { useCredits } from '../../../contexts/CreditsContext';
import { useBook } from '../../../contexts/BookContext';
import { auth } from '../../../config/firebase';
import { getCloudFunctionUrl } from '../../../config/firebaseFunctionUrl';
import { visualStyles } from '../../../data/styleOptions';
import { updateSingleElementPrompt } from '../../../services/bookService/operations/updateSingleElementPrompt';
import { db } from '../../../config/firebase';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import BorderEnableCheckboxV2 from './BorderEnableCheckboxV2';
import WorldObjectsPanel from './WorldObjectsPanel';
import WorldObjectsConsistencyModal from './WorldObjectsConsistencyModal';
import { filterRelevantWorldObjects } from '../../../utils/filterWorldObjects';
import { getImportantObjects } from '../../../utils/getImportantObjects';
import StyleDropdown from './StyleDropdown';

export default function ImageElementControlPanel({
  element,
  onOpenImageEditor,
  onOpenBorderEditor,
  onSaveSpread,
  onGeneratingChange,
  onUpdate,
  showWorldObjects,
}) {
  const { isUploading, handleFileUpload } = useImageUpload();
  const { checkCredits } = useCredits();
  const { currentBook } = useBook();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [selectedVisualStyle, setSelectedVisualStyle] = useState('');
  const [selectedOrientation, setSelectedOrientation] = useState('portrait');
  const [activeTab, setActiveTab] = useState('generate');
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  const [hasActiveJob, setHasActiveJob] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [borderColor, setBorderColor] = useState(element?.borderColor || '#ffffff');
  const [borderWidth, setBorderWidth] = useState(element?.borderWidth || 8);
  const [borderStyle, setBorderStyle] = useState(element?.borderStyle || 'solid');
  const [borderRadius, setBorderRadius] = useState(element?.borderRadius || 8);
  const [showConsistencyModal, setShowConsistencyModal] = useState(false);
  const [pendingGenerationData, setPendingGenerationData] = useState(null);
  
  // Calculate borderEnabled from element (for custom checkbox component)
  const borderEnabled = element?.hasBorder !== false ? true : false;

  // Notify parent when generating state changes
  useEffect(() => {
    if (onGeneratingChange) {
      onGeneratingChange(isGenerating ? element?.id : null);
    }
  }, [isGenerating, element?.id, onGeneratingChange]);

  // Listen for active image generation jobs for this element
  useEffect(() => {
    if (!element?.id || !currentBook?.id) return;

    const jobsRef = collection(db, 'imageUpdateJobs');
    const q = query(
      jobsRef,
      where('elementId', '==', element.id),
      where('bookId', '==', currentBook.id),
      where('status', 'in', ['pending', 'received', 'retrying'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setHasActiveJob(!snapshot.empty);
    }, (err) => {
      console.warn('[ImageElementControlPanel] Error querying jobs:', err.message);
    });

    return () => unsubscribe();
  }, [element?.id, currentBook?.id]);

  // ✨ NEW: Listen for real-time element changes (uploaded/generated images)
  useEffect(() => {
    if (!element?.id || !currentBook?.id) return;

    const spreadId = element.spreadId || 'spread-0';
    
    // Listen to book document which contains nested spreads[spreadId].elements
    const bookRef = doc(db, 'books', currentBook.id);
    const unsubscribe = onSnapshot(bookRef, (snapshot) => {
      if (snapshot.exists()) {
        const bookData = snapshot.data();
        const spreads = bookData.spreads || {};
        const spread = spreads[spreadId] || {};
        const elements = Array.isArray(spread.elements) ? spread.elements : [];
        
        // Find our element in the spreads array
        const updatedElement = elements.find(el => 
          (el.id || el.docId) === (element.id || element.docId)
        );
        
        if (updatedElement) {
          // Check if images array changed
          const imagesChanged = updatedElement.images && JSON.stringify(updatedElement.images) !== 
                               JSON.stringify(element.images);
          // Check if orientation changed
          const orientationChanged = updatedElement.orientation !== element.orientation;
          // Check if prompt data changed
          const imagePromptsChanged = updatedElement.imagePrompts &&
            JSON.stringify(updatedElement.imagePrompts) !== JSON.stringify(element.imagePrompts);
          const imagePromptChanged = updatedElement.imagePrompt !== element.imagePrompt;
          
          if (imagesChanged || orientationChanged || imagePromptsChanged || imagePromptChanged) {
            const updates = {};
            if (imagesChanged) {
              updates.images = updatedElement.images;
              console.log('[ImageElementControlPanel] Images updated, refreshing Collection tab:', {
                elementId: element.id,
                newImageCount: updatedElement.images.length,
                oldImageCount: element.images?.length || 0
              });
            }
            if (orientationChanged) {
              updates.orientation = updatedElement.orientation;
              console.log('[ImageElementControlPanel] Orientation updated:', {
                elementId: element.id,
                oldOrientation: element.orientation,
                newOrientation: updatedElement.orientation
              });
            }
            if (imagePromptsChanged) {
              updates.imagePrompts = updatedElement.imagePrompts;
              console.log('[ImageElementControlPanel] Prompts updated:', {
                elementId: element.id,
                newPromptCount: updatedElement.imagePrompts.length,
                oldPromptCount: element.imagePrompts?.length || 0
              });
            }
            if (imagePromptChanged) {
              updates.imagePrompt = updatedElement.imagePrompt;
              console.log('[ImageElementControlPanel] imagePrompt updated:', {
                elementId: element.id,
                newImagePrompt: updatedElement.imagePrompt?.slice(0, 50)
              });
            }
            // Trigger update to refresh element with new data
            onUpdate(updates);
          }
        }
      }
    }, (err) => {
      console.warn('[ImageElementControlPanel] Error listening to element changes:', err.message);
    });

    return () => unsubscribe();
  }, [element?.id, element?.spreadId, currentBook?.id, element?.images]);

  // Initialize prompt when element changes
  useEffect(() => {
    if (element?.imagePrompts && Array.isArray(element.imagePrompts)) {
      setCurrentPromptIndex(Math.max(0, element.imagePrompts.length - 1));
      setEditedPrompt(element.imagePrompts[Math.max(0, element.imagePrompts.length - 1)] || '');
    } else if (element?.imagePrompt) {
      setEditedPrompt(element.imagePrompt);
      setCurrentPromptIndex(0);
    } else {
      setEditedPrompt('');
      setCurrentPromptIndex(0);
    }

    setSelectedVisualStyle(currentBook?.visualStyle?.value || 'Digital SciFi Art');
    setSelectedOrientation(element?.orientation || 'portrait');
  }, [element, currentBook]);

  // Sync border state with element changes (only when selecting a different element)
  useEffect(() => {
    if (element) {
      setBorderColor(element.borderColor || '#ffffff');
      setBorderWidth(element.borderWidth || 8);
      setBorderStyle(element.borderStyle || 'solid');
      setBorderRadius(element.borderRadius || 8);
    }
  }, [element?.id]);

  if (!element || element.type !== 'image') return null;

  const GENERATION_COST = 5;

  // ✨ NEW: Separate generated vs uploaded images for collection display
  const generatedImages = element?.images?.filter(img => !img.type || img.type === 'generated') || [];
  const uploadedImages = element?.images?.filter(img => img.type === 'uploaded') || [];

  const handleFileSelect = async (file) => {
    try {
      setError('');
      
      // ✨ NEW: Pass element context for auto-resizing
      const elementContext = {
        elementId: element.id || element.docId,
        bookId: currentBook?.id,
        spreadId: element.spreadId || 'spread-0',
        currentSize: element.size,  // { width, height }
        canonicalDiagonal: element.canonicalDiagonal,
        orientation: element.orientation || 'portrait'
      };
      
      const imageUrl = await handleFileUpload(file, elementContext);
      if (imageUrl) {
        // ✨ NEW: Show success toast - Firestore is already updated by Cloud Function
        notify.success('✓ Image uploaded & resized successfully', { toastId: 'image-upload-success' });
        console.log('Image uploaded and resized successfully:', imageUrl);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file. Please try again.');
      notify.error('Failed to upload image', { toastId: 'image-upload-error' });
    }
  };

  const handleGenerateImage = async () => {
    if (!editedPrompt.trim()) {
      setError('Please enter a prompt to generate an image.');
      return;
    }

    try {
      const hasCredits = await checkCredits(GENERATION_COST);
      if (!hasCredits) {
        setError('Insufficient credits. Please purchase more credits.');
        return;
      }

      const generationData = {
        bookId: currentBook?.id,
        elementId: element.id || element.docId,
        spreadId: element.spreadId || 'spread-0',
        costCredits: GENERATION_COST,
        orientation: selectedOrientation,
        position: element.position || null,
        promptIndex: element.imagePrompts?.length || 0,
        prompt: editedPrompt,
        artStyle: selectedVisualStyle,
        bookContext: currentBook,
      };

      // Check for missing important world object images
      if (currentBook?.world) {
        const importantObjects = getImportantObjects(currentBook.world);
        const totalObjects = importantObjects.length;
        const missingImages = importantObjects.filter(obj => !obj.referenceImage).length;

        console.log('[ImageElementControlPanel] Important objects check:', { totalObjects, missingImages, importantObjects });

        // If there are missing images for important objects, show the modal and wait for user choice
        if (missingImages > 0) {
          console.log('[ImageElementControlPanel] Showing consistency modal');
          setPendingGenerationData(generationData);
          setShowConsistencyModal(true);
          return;
        }
      }

      // All objects have reference images, proceed with generation
      await proceedWithImageGeneration(generationData);
    } catch (error) {
      console.error('Error generating image:', error);
      setError('Failed to generate image. Please try again.');
    }
  };

  const proceedWithImageGeneration = async (jobData) => {
    try {
      setIsGenerating(true);
      setError('');

      const user = auth?.currentUser;
      if (!user) {
        throw new Error('You must be signed in to generate images');
      }
      const token = await user.getIdToken();

      const jobResp = await fetch(
        getCloudFunctionUrl('createImageUpdateJob'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(jobData),
        }
      );

      const jobJson = await jobResp.json().catch(() => ({}));
      if (!jobResp.ok || !jobJson?.jobId) {
        if (jobResp.status === 402) {
          throw new Error(jobJson?.error || 'Insufficient credits');
        }
        throw new Error(jobJson?.error || 'Failed to create image job');
      }

      onUpdate({
        imageUpdateJobId: jobJson.jobId,
        imageGenerationStatus: 'pending',
        visualStyle: selectedVisualStyle,
        orientation: selectedOrientation,
      });
    } catch (error) {
      console.error('Error generating image:', error);
      setError('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const imagePrompts = element?.imagePrompts || [];
  const totalPrompts = imagePrompts.length;

  const goToPreviousPrompt = () => {
    setCurrentPromptIndex(prev => Math.max(0, prev - 1));
    setEditedPrompt(imagePrompts[Math.max(0, currentPromptIndex - 1)] || '');
  };

  const goToNextPrompt = () => {
    setCurrentPromptIndex(prev => Math.min(imagePrompts.length - 1, prev + 1));
    setEditedPrompt(imagePrompts[Math.min(imagePrompts.length - 1, currentPromptIndex + 1)] || '');
  };

  const handleUpdatePrompt = async () => {
    if (!currentBook?.id) {
      setError('Book not found');
      return;
    }

    if (!element?.id && !element?.docId) {
      setError('Element not found');
      return;
    }

    const spreadId = element?.spreadId || 'spread-0';

    try {
      setIsUpdatingPrompt(true);
      setError('');
      console.log('Updating image prompt for element:', element?.id || element?.docId);

      // Save the spread FIRST before generating prompt (same pattern as generateImage)
      // This ensures the element is persisted and available for the Firebase function
      if (onSaveSpread) {
        console.log('[ImageElementControlPanel UPDATE_PROMPT] Saving spread before prompt generation...');
        const saveResult = await onSaveSpread();
        console.log('[ImageElementControlPanel UPDATE_PROMPT] Spread saved:', saveResult);
      } else {
        console.warn('[ImageElementControlPanel UPDATE_PROMPT] onSaveSpread is NOT defined, proceeding without save');
      }

      const result = await updateSingleElementPrompt(currentBook.id, spreadId, element?.id || element?.docId);
      console.log('Update single prompt result:', result);

      const promptResult = /** @type { { success?: boolean, allPrompts?: string[], error?: string } | undefined } */ (result);

      if (promptResult?.success) {
        // Update local state with new prompts
        if (promptResult.allPrompts && Array.isArray(promptResult.allPrompts)) {
          const newPrompt = promptResult.allPrompts[promptResult.allPrompts.length - 1];
          setEditedPrompt(newPrompt);
          setCurrentPromptIndex(promptResult.allPrompts.length - 1);
          
          // Update the element in the parent component
          onUpdate({
            imagePrompts: promptResult.allPrompts
          });
          
          // Show success toast
          notify.success('New image prompt generated successfully', { toastId: 'image-prompt-success' });
        }
      } else {
        const errorMsg = promptResult?.error || 'Failed to update image prompt';
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      const errorMessage = error?.message || error?.code || 'Failed to update image prompt. Please try again.';
      setError(`Error: ${errorMessage}`);
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  return (
    <>
      {/* World Objects Consistency Modal */}
      <WorldObjectsConsistencyModal
        isOpen={showConsistencyModal}
        onClose={() => {
          setShowConsistencyModal(false);
          setPendingGenerationData(null);
        }}
        onContinue={() => {
          setShowConsistencyModal(false);
          if (pendingGenerationData) {
            proceedWithImageGeneration(pendingGenerationData);
          }
          setPendingGenerationData(null);
        }}
        onShowWorldObjects={() => {
          setShowConsistencyModal(false);
          // This will be handled by parent component to show world objects
          onUpdate?.({ showWorldObjects: true });
        }}
        missingImageCount={
          pendingGenerationData
            ? (() => {
                const importantObjects = getImportantObjects(currentBook?.world);
                return importantObjects.filter(obj => !obj.referenceImage).length;
              })()
            : 0
        }
        totalCount={
          pendingGenerationData
            ? (() => {
                const importantObjects = getImportantObjects(currentBook?.world);
                return importantObjects.length;
              })()
            : 0
        }
      />

      <div className="space-y-4">
        {/* Error Message */}
        {error && (
          <div className="p-2 bg-red-50 text-red-700 rounded text-xs">
            {error}
          </div>
        )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('upload')}
            className={`py-2 px-1 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'upload'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Upload
          </button>
          <button
            onClick={() => setActiveTab('generate')}
            className={`py-2 px-1 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'generate'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Generate
          </button>
          {element.images && element.images.length > 0 && (
            <button
              onClick={() => setActiveTab('collection')}
              className={`py-2 px-1 text-xs font-medium border-b-2 transition-colors ${
                activeTab === 'collection'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Collection
            </button>
          )}
          <button
            onClick={() => setActiveTab('borders')}
            className={`py-2 px-1 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'borders'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Borders
          </button>
        </div>
      </div>

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="space-y-3">
          <label className="flex items-center justify-center w-full px-3 py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleFileSelect(e.target.files[0]);
                }
              }}
              disabled={isUploading}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-2 text-xs text-gray-600">
              <PhotoIcon className="h-6 w-6" />
              {isUploading ? 'Uploading...' : 'Click to upload image'}
            </div>
          </label>
        </div>
      )}

      {/* Generate Tab */}
      {activeTab === 'generate' && (
        <div className="space-y-3">
          {/* Image Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-700">Image Prompt</label>
              {totalPrompts > 0 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={goToPreviousPrompt}
                    disabled={currentPromptIndex === 0}
                    className="p-1 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                  >
                    <ChevronLeftIcon className="h-3 w-3" />
                  </button>
                  <span className="text-xs font-medium text-gray-700 min-w-[30px] text-center">
                    {currentPromptIndex + 1} of {totalPrompts}
                  </span>
                  <button
                    onClick={goToNextPrompt}
                    disabled={currentPromptIndex === totalPrompts - 1}
                    className="p-1 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                  >
                    <ChevronRightIcon className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <textarea
              value={editedPrompt}
              onChange={(e) => setEditedPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              className="w-full min-h-[80px] p-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Update Prompt Button */}
          <button
            onClick={handleUpdatePrompt}
            disabled={isUpdatingPrompt}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 text-white text-xs font-medium rounded-lg transition-colors"
          >
            {isUpdatingPrompt ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Updating prompt...
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Generate New Image Prompt 
              </>
            )}
          </button>

          {/* Visual Style */}
          <StyleDropdown
            selectedStyleValue={selectedVisualStyle}
            onStyleValueChange={setSelectedVisualStyle}
            label="Visual Style"
            showDescription={true}
          />

          {/* Orientation */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Orientation</label>
            <select
              value={selectedOrientation}
              onChange={(e) => setSelectedOrientation(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
              <option value="square_hd">Square HD</option>
              <option value="square">Square</option>
              <option value="portrait_4_3">Portrait 3:4</option>
              <option value="portrait_16_9">Portrait 9:16</option>
              <option value="landscape_4_3">Landscape 4:3</option>
              <option value="landscape_16_9">Landscape 16:9</option>
            </select>
            <p className="text-xs text-gray-500 mt-0.5">Choose the orientation for your image</p>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerateImage}
            disabled={isGenerating || !editedPrompt.trim() || hasActiveJob}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white text-xs font-medium rounded-lg transition-colors"
          >
            <SparklesIcon className="h-4 w-4" />
            {isGenerating ? 'Generating...' : hasActiveJob ? 'Image Generating...' : 'Generate Image'}
          </button>
        </div>
      )}

       {/* Collection Tab */}
       {activeTab === 'collection' && element.images && element.images.length > 0 && (
         <div className="space-y-3">
           {/* Image Display */}
           <div className="relative bg-gray-100 rounded-lg overflow-hidden border border-gray-200 h-[300px] flex items-center justify-center">
             <img
               src={element.images[currentImageIndex]?.url}
               alt={`Image ${currentImageIndex + 1}`}
               className="max-w-full max-h-full object-contain"
             />
           </div>

           {/* Image Navigation */}
           <div className="flex items-center justify-between gap-2">
             <button
               onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
               disabled={currentImageIndex === 0}
               className="p-2 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
             >
               <ChevronLeftIcon className="h-4 w-4" />
             </button>
             <span className="text-xs font-medium text-gray-700">
               {currentImageIndex + 1} of {element.images.length}
             </span>
             <button
               onClick={() => setCurrentImageIndex(prev => Math.min(element.images.length - 1, prev + 1))}
               disabled={currentImageIndex === element.images.length - 1}
               className="p-2 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
             >
               <ChevronRightIcon className="h-4 w-4" />
             </button>
           </div>

           {/* Image Metadata */}
           <div className="border-t pt-3 space-y-2">
             {/* Prompt (only for generated images) */}
             {element.images[currentImageIndex]?.promptIndex !== undefined && element.imagePrompts?.[element.images[currentImageIndex].promptIndex] && (
               <div className="bg-gray-50 rounded p-2">
                 <div className="text-xs font-medium text-gray-600 mb-1">Prompt</div>
                 <p className="text-xs text-gray-700 line-clamp-3">
                   {element.imagePrompts[element.images[currentImageIndex].promptIndex]}
                 </p>
               </div>
             )}

             {/* Settings Grid */}
             <div className="grid grid-cols-2 gap-2">
               {element.images[currentImageIndex]?.visualStyle && (
                 <div className="bg-gray-50 rounded p-2">
                   <div className="text-xs font-medium text-gray-600 mb-1">Visual Style</div>
                   <div className="text-xs text-gray-900 font-semibold">
                     {(() => {
                       const styleObj = visualStyles.find(s => s.value === element.images[currentImageIndex].visualStyle);
                       return styleObj?.title || element.images[currentImageIndex].visualStyle;
                     })()}
                   </div>
                 </div>
               )}
               {element.images[currentImageIndex]?.orientation && (
                 <div className="bg-gray-50 rounded p-2">
                   <div className="text-xs font-medium text-gray-600 mb-1">Orientation</div>
                   <div className="text-xs text-gray-900 font-semibold capitalize">
                     {element.images[currentImageIndex].orientation}
                   </div>
                 </div>
               )}
               {/* ✨ NEW: Show image type badge for uploaded images */}
               {element.images[currentImageIndex]?.type === 'uploaded' && (
                 <div className="bg-blue-50 rounded p-2">
                   <div className="text-xs font-medium text-gray-600 mb-1">Source</div>
                   <div className="text-xs text-blue-900 font-semibold">
                     Uploaded
                   </div>
                 </div>
               )}
             </div>
           </div>

             {/* Set as Current Button */}
             <button
               onClick={async () => {
                 console.log('[ImageElementControlPanel] Set as Current Image clicked');
                 const selectedImage = element.images[currentImageIndex];
                 console.log('[ImageElementControlPanel] selectedImage:', selectedImage);
                 
                 try {
                   // ✨ NEW: Detect image dimensions for auto-resize
                   console.log('[ImageElementControlPanel] Loading image:', selectedImage.url);
                   const dimensions = await new Promise((resolve, reject) => {
                     const img = new Image();
                     img.onload = () => {
                       console.log('[ImageElementControlPanel] Image loaded, dimensions:', img.naturalWidth, img.naturalHeight);
                       resolve({
                         width: img.naturalWidth,
                         height: img.naturalHeight,
                         aspectRatio: img.naturalWidth / img.naturalHeight
                       });
                     };
                     img.onerror = () => {
                       console.error('[ImageElementControlPanel] Image failed to load');
                       reject(new Error('Failed to load image'));
                     };
                     img.src = selectedImage.url;
                   });
                   
                   // ✨ NEW: Get canonical diagonal (or calculate from current size)
                   let canonicalDiagonal = element.canonicalDiagonal;
                   
                   // If no canonical diagonal, calculate from current element size
                   if (!canonicalDiagonal && element.size?.width && element.size?.height) {
                     canonicalDiagonal = Math.sqrt(
                       element.size.width * element.size.width + 
                       element.size.height * element.size.height
                     );
                     console.log('[ImageElementControlPanel] Calculated canonical diagonal from current size:', canonicalDiagonal?.toFixed(1));
                   } else {
                     console.log('[ImageElementControlPanel] Using stored canonicalDiagonal:', canonicalDiagonal?.toFixed(1));
                   }
                   
                   // ✨ NEW: Calculate new size (if we have diagonal)
                   let newSize = null;
                   if (canonicalDiagonal && dimensions.width && dimensions.height) {
                     const width = Math.sqrt(
                       (canonicalDiagonal * canonicalDiagonal) / 
                       (1 + (1 / (dimensions.aspectRatio * dimensions.aspectRatio)))
                     );
                     const height = width / dimensions.aspectRatio;
                     newSize = { width: Math.round(width), height: Math.round(height) };
                     
                     console.log('[ImageElementControlPanel] Auto-resizing element:', {
                       elementId: element.id,
                       imageDimensions: dimensions,
                       canonicalDiagonal: canonicalDiagonal?.toFixed(1),
                       calculatedSize: newSize
                     });
                   } else {
                     console.log('[ImageElementControlPanel] No canonical diagonal or dimensions, skipping resize');
                   }
                   
                   // ✨ NEW: Update element with new size
                   console.log('[ImageElementControlPanel] Calling onUpdate with:', {
                     src: selectedImage.url,
                     size: newSize,
                     visualStyle: selectedImage.visualStyle,
                     orientation: selectedImage.orientation
                   });
                   onUpdate({
                     src: selectedImage.url,
                     ...(newSize && { size: newSize }),
                     visualStyle: selectedImage.visualStyle,
                     orientation: selectedImage.orientation
                   });
                 } catch (error) {
                   console.error('[ImageElementControlPanel] Error setting current image:', error);
                   // Fallback: just set src without resizing
                   console.log('[ImageElementControlPanel] Falling back to non-resize update');
                   onUpdate({
                     src: selectedImage.url,
                     visualStyle: selectedImage.visualStyle,
                     orientation: selectedImage.orientation
                   });
                 }
               }}
              className="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              Set as Current Image
            </button>

           {/* ✨ NEW: Separate Generated and Uploaded Images */}

           {/* Generated Images Section */}
           {generatedImages.length > 0 && (
             <div className="border-t pt-3">
               <p className="text-xs font-medium text-gray-600 mb-2">All Generated Images</p>
               <div className="grid grid-cols-4 gap-2">
                 {generatedImages.map((img, idx) => {
                   const fullIndex = element.images.indexOf(img);
                   return (
                     <button
                       key={fullIndex}
                       onClick={() => setCurrentImageIndex(fullIndex)}
                       className={`relative rounded overflow-hidden border-2 transition-colors ${
                         currentImageIndex === fullIndex ? 'border-indigo-600' : 'border-gray-200 hover:border-gray-300'
                       }`}
                     >
                       <img
                         src={img.url}
                         alt={`Generated ${idx + 1}`}
                         className="w-full h-16 object-cover"
                       />
                     </button>
                   );
                 })}
               </div>
             </div>
           )}

           {/* Uploaded Images Section */}
           {uploadedImages.length > 0 && (
             <div className="border-t pt-3">
               <p className="text-xs font-medium text-gray-600 mb-2">Uploaded Images</p>
               <div className="grid grid-cols-4 gap-2">
                 {uploadedImages.map((img, idx) => {
                   const fullIndex = element.images.indexOf(img);
                   return (
                     <button
                       key={fullIndex}
                       onClick={() => setCurrentImageIndex(fullIndex)}
                       className={`relative rounded overflow-hidden border-2 transition-colors ${
                         currentImageIndex === fullIndex ? 'border-indigo-600' : 'border-gray-200 hover:border-gray-300'
                       }`}
                     >
                       <img
                         src={img.url}
                         alt={`Uploaded ${idx + 1}`}
                         className="w-full h-16 object-cover"
                       />
                     </button>
                   );
                 })}
               </div>
             </div>
           )}
         </div>
       )}

      {/* Borders Tab */}
      {activeTab === 'borders' && (
        <div className="space-y-4">
          {/* Enable Border Checkbox V2 */}
          <BorderEnableCheckboxV2
            isEnabled={borderEnabled}
            onToggle={(newCheckedState) => {
              onUpdate({
                hasBorder: newCheckedState,
                borderColor: borderColor,
                borderWidth: borderWidth,
                borderStyle: borderStyle,
                borderRadius: borderRadius
              });
            }}
          />

          {borderEnabled && (
            <>
              {/* Border Color */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-3">Border Color</label>
                <div className="w-full">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                    <HexColorPicker 
                      color={borderColor} 
                      onChange={(color) => {
                        setBorderColor(color);
                        onUpdate({
                          hasBorder: borderEnabled,
                          borderColor: color,
                          borderWidth: borderWidth,
                          borderStyle: borderStyle,
                          borderRadius: borderRadius
                        });
                      }}
                      style={{ width: '100%', height: '200px' }}
                    />
                    <div className="mt-4 flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-600">HEX</span>
                      <input
                        type="text"
                        value={borderColor.toUpperCase()}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (/^#[0-9A-F]{6}$/i.test(val)) {
                            setBorderColor(val);
                            onUpdate({
                              hasBorder: borderEnabled,
                              borderColor: val,
                              borderWidth: borderWidth,
                              borderStyle: borderStyle,
                              borderRadius: borderRadius
                            });
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Width */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">Border Width</label>
                  <span className="text-xs font-medium text-gray-600">{borderWidth}px</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={borderWidth}
                  onChange={(e) => {
                    const newWidth = parseInt(e.target.value);
                    setBorderWidth(newWidth);
                    onUpdate({
                      hasBorder: borderEnabled,
                      borderColor: borderColor,
                      borderWidth: newWidth,
                      borderStyle: borderStyle,
                      borderRadius: borderRadius
                    });
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Border Style */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Border Style</label>
                <select
                  value={borderStyle}
                  onChange={(e) => {
                    setBorderStyle(e.target.value);
                    onUpdate({
                      hasBorder: borderEnabled,
                      borderColor: borderColor,
                      borderWidth: borderWidth,
                      borderStyle: e.target.value,
                      borderRadius: borderRadius
                    });
                  }}
                  className="w-full p-2 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                  <option value="double">Double</option>
                </select>
              </div>

              {/* Border Radius */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">Border Radius</label>
                  <span className="text-xs font-medium text-gray-600">{borderRadius}px</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="8"
                  value={borderRadius}
                  onChange={(e) => {
                    const newRadius = parseInt(e.target.value);
                    setBorderRadius(newRadius);
                    onUpdate({
                      hasBorder: borderEnabled,
                      borderColor: borderColor,
                      borderWidth: borderWidth,
                      borderStyle: borderStyle,
                      borderRadius: newRadius
                    });
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

            </>
          )}
        </div>
      )}

      </div>
    </>
  );
}

export { WorldObjectsPanel };
